/**
 * ReviewsList
 * List of reviews with pagination, filtering, and sorting
 */

import { Button } from '@shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select'
import type { JobReview } from '@shared/types'
import { ChevronLeft, ChevronRight, MessageSquare, Star } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ReviewCard, ReviewCardSkeleton } from './ReviewCard'

// ============================================================================
// Types
// ============================================================================

interface ReviewsListProps {
  reviews: JobReview[]
  isLoading?: boolean
  currentUserId?: string // To determine if user can delete review
  onDeleteReview?: (reviewId: string) => void
  itemsPerPage?: number
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest'
type FilterOption = 'all' | '5' | '4' | '3' | '2' | '1'

// ============================================================================
// Component
// ============================================================================

export function ReviewsList({
  reviews,
  isLoading = false,
  currentUserId,
  onDeleteReview,
  itemsPerPage = 10,
}: ReviewsListProps) {
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [filterBy, setFilterBy] = useState<FilterOption>('all')

  // Filter reviews by rating
  const filteredReviews = useMemo(() => {
    if (filterBy === 'all') return reviews

    const targetRating = parseInt(filterBy)
    return reviews.filter((review) => review.rating === targetRating)
  }, [reviews, filterBy])

  // Sort reviews
  const sortedReviews = useMemo(() => {
    const sorted = [...filteredReviews]

    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      case 'highest':
        return sorted.sort((a, b) => b.rating - a.rating)
      case 'lowest':
        return sorted.sort((a, b) => a.rating - b.rating)
      default:
        return sorted
    }
  }, [filteredReviews, sortBy])

  // Pagination
  const totalPages = Math.ceil(sortedReviews.length / itemsPerPage)
  const startIndex = (page - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReviews = sortedReviews.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  const handleFilterChange = (value: FilterOption) => {
    setFilterBy(value)
    setPage(1)
  }

  const handleSortChange = (value: SortOption) => {
    setSortBy(value)
    setPage(1)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <ReviewCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  // Empty state
  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">Sin reseñas aún</h3>
        <p className="text-muted-foreground">
          Sé el primero en dejar una reseña para este profesional.
        </p>
      </div>
    )
  }

  // No results after filtering
  if (filteredReviews.length === 0) {
    return (
      <div>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">

          <Select value={filterBy} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las calificaciones</SelectItem>
              <SelectItem value="5">5 estrellas</SelectItem>
              <SelectItem value="4">4 estrellas</SelectItem>
              <SelectItem value="3">3 estrellas</SelectItem>
              <SelectItem value="2">2 estrellas</SelectItem>
              <SelectItem value="1">1 estrella</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Más recientes</SelectItem>
              <SelectItem value="oldest">Más antiguas</SelectItem>
              <SelectItem value="highest">Mayor calificación</SelectItem>
              <SelectItem value="lowest">Menor calificación</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* No results message */}
        <div className="text-center py-12">
          <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No se encontraron reseñas</h3>
          <p className="text-muted-foreground">
            No hay reseñas que coincidan con los filtros seleccionados.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setFilterBy('all')
              setSortBy('newest')
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters and Sort */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          {/* Filter by Rating */}
          <Select value={filterBy} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-1/2 overflow-hidden">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las calificaciones</SelectItem>
              <SelectItem value="5">5 estrellas</SelectItem>
              <SelectItem value="4">4 estrellas</SelectItem>
              <SelectItem value="3">3 estrellas</SelectItem>
              <SelectItem value="2">2 estrellas</SelectItem>
              <SelectItem value="1">1 estrella</SelectItem>
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select value={sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-1/2 overflow-hidden">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Más recientes</SelectItem>
              <SelectItem value="oldest">Más antiguas</SelectItem>
              <SelectItem value="highest">Mayor calificación</SelectItem>
              <SelectItem value="lowest">Menor calificación</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <p className="text-sm text-muted-foreground text-right">
          Mostrando {startIndex + 1}-{Math.min(endIndex, sortedReviews.length)} de{' '}
          {sortedReviews.length} reseña{sortedReviews.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {paginatedReviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            canEdit={currentUserId === review.userId}
            onDelete={onDeleteReview}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }).map((_, i) => {
              const pageNum = i + 1
              // Show first, last, current, and adjacent pages
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="w-10"
                  >
                    {pageNum}
                  </Button>
                )
              } else if (pageNum === page - 2 || pageNum === page + 2) {
                return (
                  <span key={pageNum} className="px-2">
                    ...
                  </span>
                )
              }
              return null
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
